import { useTranslation } from "@me8eon/translation";
import { EnvironmentConfig } from "@me8eon/config";
import { useJwtToken } from "@me8eon/react_authentication";
import { Icon } from "@me8eon/icons";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import React, { useState } from "react";
import axios from "axios";
import "./IntuneFixHomepage.widget.scss";

type Device = {
  deviceId: string;
  deviceName: string;
  hardwareType?: string;
  lastSessionEvent?: string;
};

interface Props {
  values: {
    _meta?: {
      widget?: {
        props?: {
          deviceId?: string;
          deviceName?: string;
          devices?: Device[];
        };
      };
    };
  };
}

const GRAPH_BACKEND_URL = `${EnvironmentConfig.gatewayURL}/service/graph`;
const DEFAULT_GROUP_NAME = "homepage-change";

export const IntuneFixHomepageWidget: React.FC<Props> = ({ values }) => {
  const jwtTokenFn = useJwtToken();
  const t = useTranslation();
  const changeHomepageInstructions = t("chatBot.intune.homepageBrowserChange.changeHomepageInstructions");

  const keyMap: Record<string, string> = {
  "graph.homepage.enabled": "chatBot.intune.homepageBrowserChange.homepageEnabled",
  "graph.homepage.already-enabled": "chatBot.intune.homepageBrowserChange.homepageAlreadyEnabled",
  "graph.homepage.enable-failed": "chatBot.intune.homepageBrowserChange.homepageEnableFailed",
  "graph.homepage.removed": "chatBot.intune.homepageBrowserChange.homepageRemoved",
};

const resolveMessage = (messageKey?: string, fallback?: string): { key?: string; fallback?: string } => {
  const mapped = messageKey ? keyMap[messageKey] : undefined;
  return mapped ? { key: mapped } : { fallback: fallback || undefined };
};

  const [loading, setLoading] = useState(false);
  const [pendingDeviceId, setPendingDeviceId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [message, setMessage] = useState<{ key?: string; fallback?: string }>({});
  const [showChangeHomepageInstructions, setShowChangeHomepageInstructions] = useState(false);
  const [confirmedDeviceName, setConfirmedDeviceName] = useState("");
  const [successMessageKey, setSuccessMessageKey] = useState<string | undefined>(undefined);

  const deviceId = values?._meta?.widget?.props?.deviceId;
  const deviceName = values?._meta?.widget?.props?.deviceName;
  const devices = values?._meta?.widget?.props?.devices || [];
  const [deviceNameInput, setDeviceNameInput] = useState(deviceName || "");

  const describeHardware = (device: Device) => {
    const raw = (device.hardwareType || "").toLowerCase();
    if (raw.includes("virtual") || raw.includes("vm")) return t("chatBot.intune.deviceTypeVirtualMachine");
    if (raw.includes("desktop")) return t("chatBot.intune.deviceTypeDesktop");
    if (raw.includes("laptop") || raw.includes("notebook")) return t("chatBot.intune.deviceTypeLaptop");
    return device.hardwareType || t("chatBot.intune.deviceTypeUnknown");
  };

  // Icons sourced from the shared @me8eon/icons library instead of hardcoded SVG markup.
  // Desktop/laptop devices use existing library icons (pc_mac / deviceIcon). There is no
  // dedicated "virtual machine" icon in the library yet, so it falls back to deviceIcon
  // for now - if a distinct VM icon is needed, it should be sourced from Figma (or Hari)
  // and added to frontend/modules/icons/src/icons.ts.
  const deviceIconStyle = { width: 44, height: 44 };
  const whatsNextIconStyle = { width: 20, height: 20 };

  const DeviceIcon: React.FC<{ device: Device }> = ({ device }) => {
    const raw = (device.hardwareType || "").toLowerCase();
    if (raw.includes("desktop")) {
      return <Icon name="intuneDesktop" style={deviceIconStyle} />;
    }

    if (raw.includes("virtual") || raw.includes("vm")) {
      return <Icon name="intuneVirtual" style={deviceIconStyle} />;
    }

    // laptop / notebook / unknown default
    return <Icon name="intuneLaptop" style={deviceIconStyle} />;
  };

  const describeLastActive = (device: Device) => {
    if (!device.lastSessionEvent) return undefined;

    const parsed = new Date(device.lastSessionEvent);
    if (Number.isNaN(parsed.getTime())) return undefined;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfEvent = new Date(parsed);
    startOfEvent.setHours(0, 0, 0, 0);

    const dayDiff = Math.round((startOfToday.getTime() - startOfEvent.getTime()) / 86400000);

    let whenLabel: string;
    if (dayDiff <= 0) whenLabel = t("chatBot.intune.today");
    else if (dayDiff === 1) whenLabel = t("chatBot.intune.yesterday");
    else whenLabel = `${dayDiff} ${t("chatBot.intune.daysAgo")}`;

    return `${t("chatBot.intune.lastActive")}: ${whenLabel}`;
  };

  const runFix = async (targetDeviceId: string | undefined, targetDeviceName: string) => {
    setLoading(true);
    setPendingDeviceId(targetDeviceId ?? targetDeviceName);
    setStatus("idle");
    setMessage({});
    setShowChangeHomepageInstructions(false);
    setConfirmedDeviceName("");
    setSuccessMessageKey(undefined);

    try {
      if (!targetDeviceId && !targetDeviceName.trim()) {
        setStatus("error");
        setMessage({ key: "chatBot.intune.selectOrEnterDevice" });
        return;
      }

      const payload: { groupName: string; deviceId?: string; deviceName?: string } = {
        groupName: DEFAULT_GROUP_NAME,
      };
      if (targetDeviceId) payload.deviceId = targetDeviceId;
      if (targetDeviceName.trim()) payload.deviceName = targetDeviceName.trim();

      const response = await axios.post(`${GRAPH_BACKEND_URL}/add-device-to-group`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await jwtTokenFn()}`,
        },
      });

      if (response?.data?.success) {
        setStatus("success");
        setMessage(resolveMessage(response?.data?.messageKey, response?.data?.message || "Done."));
        // Shown for both "enabled" and "already-enabled": the user still needs the Edge
        // steps to actually change the homepage, regardless of when the policy was applied.
        setShowChangeHomepageInstructions(true);
        setConfirmedDeviceName(targetDeviceName.trim());
        setSuccessMessageKey(response?.data?.messageKey);
      } else {
        setStatus("error");
        setMessage(resolveMessage(response?.data?.errorKey, response?.data?.error || "Action failed."));
      }
    } catch (error: any) {
      setStatus("error");
      setMessage(
        resolveMessage(error?.response?.data?.errorKey, error?.response?.data?.error || "Action failed.")
      );
    } finally {
      setLoading(false);
      setPendingDeviceId(undefined);
    }
  };

  // Keep blank lines (unlike a plain filter of empty strings) so translators can
  // add "\n\n" in translation.json to create paragraph spacing between steps.
  const instructionSteps = changeHomepageInstructions
    .split("\n")
    .map((line) => line.trim());

  return (
    <div className="intune-fix-homepage-widget">
      <div className="intune-header-card">
        <div className="intune-header-text">
          <div className="intune-header-title">{t("chatBot.intune.changeBrowserHomePage")}</div>
          <div className="intune-header-description">{t("chatBot.intune.homepageBrowserChange.policyDescription")}</div>
        </div>
      </div>

      {devices.length > 0 && (
        <div className="device-section">
          <div className="device-section-title">
            {devices.length === 1 ? t("chatBot.intune.fixWillBeApplied") : t("chatBot.intune.selectDevice")}
          </div>
          <div className="device-cards">
            {devices.map((d) => {
              const isPending = loading && pendingDeviceId === d.deviceId;
              const lastActive = describeLastActive(d);
              const metaParts = [describeHardware(d), lastActive].filter(Boolean);
              return (
                <div key={d.deviceId} className="device-card">
                  <div className="device-card-header">
                    <div className="device-icon">
                      <DeviceIcon device={d} />
                    </div>
                    <div className="intune-device-info">
                      <div className="device-name">{d.deviceName}</div>
                      <div className="device-meta">{metaParts.join(" • ")}</div>
                    </div>
                  </div>
                  <div className="device-card-footer">
                    <EonUiButton
                      text={
                        isPending
                          ? t("chatBot.intune.working")
                          : devices.length === 1
                            ? t("chatBot.intune.confirm")
                            : t("chatBot.intune.fixOnThis")
                      }
                      rank="primary"
                      scheme="red500"
                      size="small"
                      className="device-action"
                      onClick={() => runFix(d.deviceId, d.deviceName)}
                      disabled={loading}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {devices.length === 0 && (
        <>
          <input
            type="text"
            className="manual-device-input"
            value={deviceNameInput}
            onChange={(e) => setDeviceNameInput(e.target.value)}
            placeholder={t("chatBot.intune.enterDeviceName")}
          />
          <EonUiButton
            text={loading ? t("chatBot.intune.working") : t("chatBot.intune.fixThis")}
            rank="primary"
            scheme="red500"
            size="small"
            className="manual-device-action"
            onClick={() => runFix(deviceId, deviceNameInput)}
            disabled={loading}
          />
        </>
      )}

      {status === "error" && <div className="status-error">{message.key ? t(message.key) : message.fallback}</div>}

      {status === "success" && (
        <div className="status-success">
          <div className="success-banner">
            <span className="success-icon" aria-hidden="true">
              <Icon name="checkCircle"/>
            </span>
            <span>
              {confirmedDeviceName && successMessageKey !== "graph.homepage.already-enabled"
                ? (
                  <>
                    <strong>{t("chatBot.intune.deviceLabel")}: {confirmedDeviceName}</strong>{" "}
                    <strong>{t("chatBot.intune.homepageBrowserChange.deviceSelectedForPolicy")}</strong>
                  </>
                )
                : (message.key ? t(message.key) : message.fallback)}
            </span>
          </div>

          {successMessageKey === "graph.homepage.enabled" && (
            <div className="whats-next-card">
              <div className="whats-next-title">{t("chatBot.intune.homepageBrowserChange.whatsNextTitle")}</div>
              <ul className="whats-next-list">
                <li>
                  <span className="whats-next-icon" aria-hidden="true">
                    <Icon name="contract_alt_2" style={whatsNextIconStyle} />
                  </span>
                  <span>{t("chatBot.intune.homepageBrowserChange.whatsNextSubmitted")}</span>
                </li>
                <li>
                  <span className="whats-next-icon" aria-hidden="true">
                    <Icon name="clock_alt" style={whatsNextIconStyle} />
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: t("chatBot.intune.homepageBrowserChange.whatsNextSyncTime") }} />
                </li>
                <li>
                  <span className="whats-next-icon" aria-hidden="true">
                    <Icon name="clock" style={whatsNextIconStyle} />
                  </span>
                  <span>{t("chatBot.intune.homepageBrowserChange.whatsNextReturn")}</span>
                </li>
              </ul>
            </div>
          )}

          {showChangeHomepageInstructions && instructionSteps.length > 0 && (
            <div className="instructions-card">
              <div className="instructions-title">
                {t("chatBot.intune.homepageBrowserChange.stepsForEdge")}
              </div>
              <div className="instructions-body">{instructionSteps.join("\n")}</div>
            </div>
          )}
        </div>
      )}

      <div className="intune-action-source"><strong>Source</strong>: IT Support Agent</div>
    </div>
  );
};